import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import { createOpenBankingPayment, getOpenBankingPayment } from "@api";
import { Logo, Modal, SecondaryButton } from "@components";
import { MerchantButton, ProcessingIcon } from "../../../components";
import {
  formatAmount,
  formatSortCode,
  notifyStoreParent,
  redirectToThirdParty,
} from "@utils";
import { isMobile } from "react-device-detect";
import {
  getBankPaymentSucceededPath,
  getExternalBankFailureUrl,
} from "../hooks/flowRouting";
import {
  getPreferredOpenBankingFlowType,
  resolveOpenBankingFlowType,
} from "../../shared/openBankingPresentation";

const PRConfirmBankPayment = () => {
  const {
    methodStepData,
    merchant,
    amount,
    flowId,
    id,
    transactionIds,
    customerIsFinanced,
    source,
    checkoutId,
    customerName,
    reference,
    paymentRequestId,
    customerId,
    branchId,
    customerEmail,
    salesOrderIds,
    paymentDiscounts,
    paymentRequestToken,
    checkoutToken,
    cancelUrl,
  } = useOutletContext();

  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [payment, setPayment] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const institution = methodStepData?.institution;
  // If we somehow land on this route without a chosen institution, just render nothing.
  if (!institution) {
    return null;
  }

  const flowType = getPreferredOpenBankingFlowType(isMobile);
  const returnUrl = `/${flowId}/${id}/paybybank`;

  const onBack = () => navigate(-1);

  const onPaymentConfirmed = async () => {
    if (redirecting || polling) return;

    if (flowType === "redirect") {
      setRedirecting(true); // Show the redirect screen immediately
    } else if (flowType === "poll") {
      setPolling(true);
    }

    const callback = `${window.location.origin}/yapilycallback?p-id=`;

    // Start a timer immediately to ensure a minimum wait of 1 second
    const redirectAfter = Date.now() + 1000;

    const response = await createOpenBankingPayment(
      merchant.id,
      paymentRequestId,
      customerId,
      null, // orderNumber
      checkoutId,
      customerName,
      amount,
      reference,
      institution.id,
      callback,
      source,
      branchId,
      customerEmail,
      customerIsFinanced,
      transactionIds,
      salesOrderIds,
      paymentDiscounts,
      paymentRequestToken ?? checkoutToken ?? null
    );

    if (response?.tokenExpired) {
      setRedirecting(false);
      setSessionExpired(true);
      return;
    }

    if (response.data) {
      setPayment(response.data);
      const resolvedFlowType = resolveOpenBankingFlowType(
        flowType,
        response.data.qrUrl,
      );

      if (resolvedFlowType === "redirect") {
        setPolling(false);
        setRedirecting(true);
        const delay = Math.max(0, redirectAfter - Date.now());

        setTimeout(() => {
          redirectToThirdParty(response.data.paymentUrl);
        }, delay);
      } else if (resolvedFlowType === "poll") {
        setTimeout(pollStatus, 10000, response.data.id);
      }
    } else {
      const delay = Math.max(0, redirectAfter - Date.now());

      setTimeout(() => {
        navigate(returnUrl + "?error=bank-error");
      }, delay);
    }
  };

  const pollStatus = async (openBankingPaymentId) => {
    const paymentData = await getOpenBankingPayment(openBankingPaymentId);

    switch (paymentData.status) {
      case "Processing":
        // For this PR flow we just send the user to the generic processing route.
        navigate(
          `/${flowId}/${id}/processing?p-id=${paymentData.id}&t=openbanking`
        );
        break;
      case "Initialized":
        setTimeout(pollStatus, 1000, openBankingPaymentId);
        break;
      case "Cancelled":
      case "PaymentFailed":
        {
          const externalBankFailureUrl = getExternalBankFailureUrl({
            flowId,
            status: paymentData.status,
            cancelUrl,
            bankAuthorisationFailed: true,
          });

          if (externalBankFailureUrl) {
            // The embedding store navigates its own page from this message;
            // navigating the iframe too would run the cancel return twice.
            if (!notifyStoreParent({ type: "ontik:hosted:cancel" })) {
              window.location.replace(externalBankFailureUrl);
            }
          } else {
            navigate(`/${flowId}/${id}/paymentfailed`);
          }
        }
        break;
      case "PaymentSucceeded":
        // The checkout flow finishes on the merchant's success URL via the
        // processing page; it has no success route of its own.
        navigate(
          getBankPaymentSucceededPath({ flowId, id, paymentId: paymentData.id })
        );
        break;
      default:
        // eslint-disable-next-line no-console
        console.error("Invalid payment status");
        break;
    }
  };

  return (
    <>
    <Modal isOpen={sessionExpired} onClose={() => navigate(`/${flowId}/${id}/`)} title="Session expired">
      <div className="p-5 flex flex-col gap-4">
        <p className="text-sm text-zinc-700">Payment session expired. Please start again.</p>
        <button
          onClick={() => navigate(`/${flowId}/${id}/`)}
          className="w-full rounded-lg bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 cursor-pointer"
        >
          OK
        </button>
      </div>
    </Modal>
    <div className="lg:bg-zinc-100 h-full lg:h-auto lg:pt-12 grow flex flex-col items-center">
      {!redirecting && !polling && (
        <div className="bg-white w-full lg:max-w-md lg:mx-auto lg:rounded-lg overflow-hidden lg:border border-zinc-200 lg:shadow-sm lg:mb-12 flex flex-col items-center h-full lg:h-auto">
          <div className="flex-1 overflow-y-auto lg:overflow-visible flex flex-col items-center px-5 py-4 pb-5 gap-9 lg:pb-4 max-w-md">
            {/* Heading */}
            <div className="w-full flex flex-col">
              <h2 className="font-semibold text-xl/8">We need your consent</h2>
              <p className="text-zinc-700 text-sm">
                We have partnered with Yapily Connect to securely initiate
                payment from your account at {institution.name}
              </p>
            </div>

            {/* Payment total */}
            <div className="w-full flex flex-col gap-1 items-center">
              <h3 className="text-sm">Payment total</h3>
              <h1 className="font-medium text-2xl">{formatAmount(amount)}</h1>
            </div>

            {/* Payee details */}
            <div className="border border-zinc-200 rounded-xl w-full max-w-md overflow-hidden p-5 flex flex-col gap-6 shadow-sm shrink-0">
              {[
                {
                  label: "Payee name",
                  value: merchant.name,
                },
                {
                  label: "Payee Account number",
                  value: merchant.accountNumber,
                },
                {
                  label: "Payee Sort code",
                  value: formatSortCode(merchant.sortCode),
                },
                { label: "Reference", value: reference },
              ].map(({ label, value }, index) => (
                <div
                  key={index}
                  className="flex justify-between border-zinc-200 text-zinc-900 text-xs"
                >
                  <span>{label}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer actions - Sticky inside container */}
          <div
            className="p-5 pb-1 bg-white border-t border-zinc-200 flex flex-col gap-1 w-full items-center
              sticky bottom-0 left-0 right-0 z-10 lg:static lg:mt-auto"
          >
            <div className="max-w-md flex flex-col items-center">
              <p className="text-xs/relaxed text-zinc-700 text-center pb-4">
                By using the service, you agree to Yapily Connect initiating
                this payment and its{" "}
                <a
                  className="text-blue-800"
                  href="https://www.yapily.com/legal/end-user-terms"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Terms &amp; Conditions
                </a>{" "}
                and{" "}
                <a
                  className="text-blue-800"
                  href="https://www.yapily.com/legal/privacy-policy"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Privacy Notice
                </a>
                .
              </p>
              <MerchantButton
                merchant={merchant}
                onClick={onPaymentConfirmed}
                loading={redirecting || polling}
              >
                Confirm
              </MerchantButton>
              <SecondaryButton
                onClick={onBack}
                className="border-none p-4 w-auto"
              >
                Cancel
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}
      {redirecting && (
        <div className="pt-16 px-12">
          <div className="h-[146px] flex items-center w-full justify-center gap-2">
            <Logo
              merchant={merchant}
              type="icon"
              className="rounded-lg"
              height="80px"
            />
            <div className="size-20 min-h-20 flex items-center justify-center">
              <ThreeDots width={40} height={40} color="#A1A1AA" />
            </div>
            <img
              className="size-20 rounded-lg"
              src={institution.iconUrl}
              alt={institution.name}
            />
          </div>
          <h1 className="text-xl text-center font-semibold mb-1.5">
            Securely transferring you to {institution.name} for authentication
          </h1>
          <p className="text-zinc-500 text-center">
            Hold tight, we’re contacting your bank to securely initiate your
            payment.
          </p>
        </div>
      )}
      {polling && payment && (
        <div className="pt-16 px-12 flex flex-col items-center">
          <p className="font-semibold mb-7">Scan to pay using mobile banking</p>
          <img className="w-1/2" src={payment.qrUrl} alt="paymentUrl" />
          <p className="font-semibold my-7">Or</p>
          <a
            href={payment.paymentUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="underline text-primary"
          >
            Continue in browser
          </a>
        </div>
      )}
      {polling && !payment && (
        <div className="w-full mt-64 flex items-center justify-center">
          <ProcessingIcon className="m-auto" merchant={merchant} />
        </div>
      )}
    </div>
    </>
  );
};

export default PRConfirmBankPayment;
